/**
 * Les clauses de consentement du mandat, servies par le serveur.
 *
 * POURQUOI CE COMPOSANT EXISTE, ET POURQUOI IL EST PARTAGÉ
 *
 * Le texte vivait en DEUX copies : `features/clients/pages/MandatPage.js` pour le CRM et
 * `features/installer/components/wizard/StepConsentements.jsx` pour le parcours terrain.
 * Elles divergeaient déjà le 28/08 — formulations différentes, une case présente d'un
 * côté et pas de l'autre. Deux textes pour un même engagement, c'est un vice de
 * consentement : le client signe l'un et le dossier prouve l'autre.
 *
 * Le texte n'est donc PAS écrit ici. Il vient de `/api/mandats/texte-consentements`, et
 * c'est de cette chaîne-là qu'on conserve l'empreinte SHA-256. Le réécrire dans l'écran,
 * même à l'identique, rendrait la preuve invérifiable : on ne saurait plus dire ce que le
 * client a réellement lu le jour où il conteste. Même discipline que le texte servi pour
 * l'accès aux données du compteur (`backend/app/services/pdl_acces.py`), et même raison.
 *
 * ⚠️ SI LE TEXTE NE PEUT PAS ÊTRE CHARGÉ, LES CASES SONT VERROUILLÉES. Faire cocher un
 * consentement dont on n'a pas pu afficher le contenu ne prouve rien et engage le
 * signataire sur un texte qu'il n'a pas vu. C'est le pendant de ce qui a été mesuré le
 * 24/08 : 70 mandats sur 70 déclaraient les CGV acceptées sans qu'aucun écran n'ait
 * jamais posé la question.
 */
import { useEffect, useState } from 'react';
import { Checkbox } from '@/shared/ui/checkbox';
import { Loader2, AlertTriangle } from 'lucide-react';
import api from '@/shared/api/client';

export default function ClausesConsentement({
  consents, setConsents, compact = false,
  /**
   * La route qui sert le texte. Une seule raison de la changer : le formulaire QR est
   * PUBLIC et n'a pas de compte, alors que `/mandats/texte-consentements` exige
   * `CLIENTS_READ`. Il lit donc `/onboarding/texte-consentements`, qui appelle la MÊME
   * fonction de service côté serveur — pas une copie du texte, une seconde porte vers
   * la même source. C'est ce qui fait que les deux ne peuvent pas rediverger.
   */
  route = '/mandats/texte-consentements',
}) {
  /**
   * ⚠️ IL Y AVAIT UN TROISIÈME PARAMÈTRE, `annexes`, ET SON RETRAIT EST VOULU (10/09/2026).
   *
   * Il permettait de greffer des cases qui NE VENAIENT PAS du texte du mandat, en deux
   * morceaux — `rendu` pour ce qu'on affiche, `etats` pour ce que la case « tout cocher »
   * coche en plus. Un seul appelant s'en servait : `BlocConsentements`, pour l'accès aux
   * données du compteur (Energrid), retiré de l'écran sur demande de Boubacar.
   *
   * Le mécanisme n'a PAS été laissé en place « au cas où ». Un `etats` sans le `rendu`
   * correspondant fait accepter d'un geste groupé un texte affiché ailleurs, ou nulle
   * part : c'est le défaut du 24/08 sur les CGV, et le garde-fou qui l'empêchait tenait
   * à la discipline de l'appelant. Sans appelant, ce garde-fou n'était plus testable —
   * un test qui ne peut plus échouer ne prouve rien. Ce que la case maîtresse coche est
   * désormais exactement ce que le serveur a servi, et rien d'autre.
   *
   * Le réintroduire pour une case neuve demande de reposer AUSSI la preuve qu'elle est
   * bien à l'écran. Voir `docs/decisions/2026-09-10-retrait-de-la-clause-energrid.md`.
   */
  const [texte, setTexte] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    api.get(route)
      .then(r => { if (vivant) setTexte(r.data); })
      .catch(() => { if (vivant) setErreur("Le texte des consentements n'a pas pu être chargé."); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [route]);

  /**
   * ⚠️ LE PRÉ-COCHAGE A ÉTÉ RETIRÉ LE 02/09, ET IL NE SE REMET PAS TEL QUEL.
   *
   * Un effet cochait ici les clauses COMMERCIALES dès que l'attestation de fin de
   * travaux était entièrement à Oui — arbitrage de Boubacar du 31/08 : « à partir du
   * moment où l'attestation de fin de chantier est validée, on considère les
   * consentements comme validés sauf cas contraire ». Les autorisations réglementaires
   * en étaient exclues, parce que le RGPD (art. 4-11) exige un « acte positif clair » et
   * qu'une case pré-cochée n'est pas un consentement.
   *
   * IL N'A PLUS D'OBJET. Les trois clauses qu'il pré-cochait — `rgpd`, `cgv`,
   * `exclusivite` — sont exactement celles que Boubacar a fait retirer le 02/09. Le
   * mécanisme ne cochait plus rien : le laisser aurait laissé croire à un confort qui
   * n'existe pas, et il se serait remis à cocher tout seul le jour où une clause
   * complémentaire reviendrait, sans que personne ne le décide.
   *
   * Ce qu'il apportait est rendu autrement, et mieux : la case « tout cocher » ci-dessous
   * est un GESTE du client, pas un état posé à sa place. C'est précisément la différence
   * que l'article 4-11 nomme.
   */

  if (chargement) {
    return (
      <div className="flex items-center gap-2 py-3" data-testid="clauses-chargement">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />
        <span className="text-[11px] text-neutral-500">Chargement du texte des consentements…</span>
      </div>
    );
  }

  if (erreur || !texte) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-2"
           data-testid="clauses-erreur">
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-[11px] text-amber-800 leading-relaxed">
          {erreur || 'Texte des consentements indisponible.'}
          <br />
          Les consentements ne peuvent pas être recueillis pour l&apos;instant : faire cocher
          une case sans afficher son contenu n&apos;engagerait le signataire sur rien.
        </div>
      </div>
    );
  }

  const taille = compact ? 'text-[11px]' : 'text-[12px]';
  const cocher = (id, valeur) => setConsents(p => ({ ...p, [id]: !!valeur }));

  /**
   * LA CASE « TOUT COCHER ». Demande de Boubacar du 02/09 : « ajouter une case pour
   * cocher tous les consentements d'un coup, et donc cocher dans la partie installateur
   * le consentement Energrid ».
   *
   * ⚠️ CE DÉPÔT AVAIT DÉJÀ RETIRÉ UN BOUTON DE CE GENRE, ET LA DIFFÉRENCE TIENT EN UN
   * MOT. Le bouton « Cocher les 4 autorisations » a disparu le 28/08 avec les clauses
   * écrites en dur : il cochait quatre cases d'un geste sur un texte que personne
   * n'avait lu, ce qui est le défaut mesuré le 24/08 sur les CGV — 70 mandats sur 70
   * déclaraient un accord jamais demandé. Le pré-cochage du 31/08 a été retiré au même
   * motif, plus haut dans ce fichier.
   *
   * Ce qui rend celle-ci acceptable, et qui doit le rester :
   *
   *   1. ELLE N'EST JAMAIS COCHÉE D'AVANCE. C'est le client qui la coche. Le RGPD
   *      (art. 4-11) interdit la case pré-cochée, pas l'acceptation groupée : ce qu'il
   *      exige est un acte positif clair, et un clic délibéré en est un.
   *   2. ELLE NE REMPLACE NI NE MASQUE RIEN. Les cases individuelles restent affichées,
   *      cochables et DÉCOCHABLES une par une, sous leur texte intégral. Décocher
   *      n'importe laquelle décoche celle-ci — sans quoi l'écran afficherait « tout
   *      accepté » sur un dossier qui ne l'est pas.
   *   3. ELLE OUVRE LA LISTE, ET C'EST UN REVIREMENT DATÉ. Elle venait en dernier
   *      jusqu'au 07/09, au motif qu'une case maîtresse placée en tête donnerait à
   *      cocher ce qui n'a pas encore été montré. Boubacar l'a fait remonter : « la case
   *      tout cocher devrait apparaître AU DÉBUT et pas en dernier ». Le motif d'origine
   *      portait sur la LECTURE, pas sur l'ordre du DOM, et il est tenu autrement : les
   *      clauses restent intégralement affichées SOUS elle, sur le même écran, et le
   *      libellé dit désormais « ci-dessous ». Laisser « ci-dessus » au-dessus de rien
   *      aurait été le vrai défaut — une case qui désigne un texte absent.
   *   4. ELLE N'EXISTE PAS SANS LE TEXTE. Les deux retours anticipés ci-dessus la
   *      couvrent comme les autres : texte indisponible, aucune case n'est rendue.
   *
   * Elle coche ce qui est À L'ÉCRAN, pas une liste recopiée : les identifiants viennent
   * du texte servi. Une clause ajoutée demain par le serveur entrera dans le geste sans
   * qu'on y pense, et une clause retirée en sortira — une liste en dur aurait fini par
   * laisser une case dehors, donc par mentir sur le mot « tout ».
   */
  const idsAffiches = [
    ...texte.autorisations.map(bloc => bloc.id),
    ...texte.complementaires.map(clause => clause.id),
  ];
  // Le périmètre de la case maîtresse est EXACTEMENT ce que le serveur a servi, depuis le
  // retrait des annexes le 10/09. Il portait en plus les cases greffées par l'appelant, et
  // c'est ce qui obligeait à vérifier qu'elles étaient bien à l'écran avant de les cocher.
  const toutEstCoche = idsAffiches.every(id => !!consents[id]);
  const toutCocher = (valeur) => {
    const valeurBooleenne = !!valeur;
    setConsents(p => {
      const suite = { ...p };
      idsAffiches.forEach(id => { suite[id] = valeurBooleenne; });
      return suite;
    });
  };

  return (
    <div className="space-y-4" data-testid="clauses-consentement">
      <div className="flex items-start justify-between gap-3">
        <p className={`${taille} font-medium text-neutral-800`}>{texte.intro}</p>
        {/* La VERSION reste écrite en base avec le consentement — c'est elle qui dit
            à quel texte le client a dit oui, et elle sera nécessaire le jour où il
            faudra le prouver. Elle ne s'affiche plus : « version v1 » à côté d'une
            case à cocher ne veut rien dire pour le client qui la lit, et Boubacar l'a
            relevé sur les deux blocs le 31/08. */}
      </div>

      <label className="flex items-start gap-3 cursor-pointer rounded-md border border-neutral-200 bg-neutral-50/60 p-3"
             data-testid="consent-tout-cocher-ligne">
        <Checkbox
          checked={toutEstCoche}
          onCheckedChange={toutCocher}
          data-testid="consent-tout-cocher"
        />
        <span className={`${taille} text-neutral-700 leading-relaxed`}>
          <span className="font-medium text-neutral-900">
            J&apos;accepte l&apos;ensemble des autorisations ci-dessous.
          </span>
          <br />
          <span className="text-neutral-500">
            Chaque case reste modifiable une par une.
          </span>
        </span>
      </label>

      {texte.autorisations.map(bloc => (
        <label key={bloc.id} className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={!!consents[bloc.id]}
            onCheckedChange={v => cocher(bloc.id, v)}
            data-testid={`consent-${bloc.id}`}
          />
          <span className={`${taille} text-neutral-600 leading-relaxed`}>
            <span className="font-medium text-neutral-800">{bloc.titre}</span>
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              {bloc.alineas.map((alinea, i) => <li key={i}>{alinea}</li>)}
            </ul>
          </span>
        </label>
      ))}

      {/* LA LISTE NOMINATIVE FAIT PARTIE DE L'AUTORISATION, pas de l'habillage.
          C'est elle qui autorise chaque société à intervenir : la replier derrière un
          « voir plus » qui ne s'ouvre jamais reviendrait à faire autoriser 33 sociétés
          sans les montrer. Elle est donc affichée, en entier, à côté des cases. */}
      <div className="rounded-md border border-neutral-200 bg-neutral-50/60 p-3 space-y-1.5"
           data-testid="clauses-partenaires">
        <p className="text-[11px] font-medium text-neutral-800">{texte.partenaires.titre}</p>
        <p className="text-[10.5px] text-neutral-500 leading-relaxed">{texte.partenaires.intro}</p>
        <p className="text-[10.5px] text-neutral-700 leading-relaxed">
          {texte.partenaires.operateurs.join(', ')}.
        </p>
        <p className="text-[10.5px] text-neutral-500 leading-relaxed">{texte.partenaires.phrase}</p>
        {/* CETTE MENTION EXISTAIT DÉJÀ DANS LE PDF SIGNÉ, et manquait à l'écran —
            relevé du 31/08. Le client cochait donc une liste qu'il croyait figée, et
            découvrait au PDF qu'elle pouvait s'allonger sans avenant. Deux documents
            qui disent deux choses du même engagement, c'est le document le moins
            engageant qui fait foi contre nous. */}
        <p className="text-[10.5px] text-neutral-500 leading-relaxed italic"
           data-testid="clauses-liste-evolutive">
          Cette liste est susceptible d&apos;évoluer. La version à jour est disponible sur
          www.ecoflexibilite.fr. Tout nouvel opérateur pourra être ajouté sans qu&apos;un
          avenant au présent mandat soit nécessaire.
        </p>
      </div>

      {texte.complementaires.map(clause => (
        <label key={clause.id} className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={!!consents[clause.id]}
            onCheckedChange={v => cocher(clause.id, v)}
            data-testid={`consent-${clause.id}`}
          />
          <span className={`${taille} text-neutral-600 leading-relaxed`}>
            {clause.texte}
            {clause.lien && (
              <>
                {' '}
                <a href={clause.lien} target="_blank" rel="noreferrer"
                   className="underline text-marque"
                   onClick={e => e.stopPropagation()}
                   data-testid={`consent-${clause.id}-lien`}>
                  {clause.lien}
                </a>
              </>
            )}
          </span>
        </label>
      ))}

      {/* ⚠️ UNE CASE DE PLUS SE RENDAIT ICI, GREFFÉE PAR L'APPELANT (`annexes.rendu`) :
          l'accès aux données du compteur, retiré de l'écran le 10/09. La liste s'arrête
          donc au texte servi par le serveur, et la case maîtresse ne coche rien d'autre.
          Le pourquoi est en tête de fichier, à côté du paramètre disparu. */}
    </div>
  );
}
